/* towsons-giveback — the claim flow.
 *
 * It knows two things about the outside world: the Worker's URL and the
 * Turnstile site key, both of which are in the page's HTML and both of which
 * are public by design. It does not know the portal's address and it holds no
 * secret, because a secret in a theme asset is a secret in a public file.
 *
 * The Turnstile token travels as a HEADER rather than a form field. That is
 * what lets the Worker check it without opening the multipart body, so the
 * upload streams through the edge untouched.
 *
 * ── THE FLOW ────────────────────────────────────────────────────────────
 *
 * Nine inputs and two file pickers is a lot to face at once, and most of them
 * only matter to somebody chasing more than 50%. So it is asked in four
 * parts: your order, the donation, anything extra, then check and send.
 *
 * The panels are hidden by a class this script adds. It never relies on
 * `hidden` in the markup, so with no JavaScript every panel is visible, the
 * submit button is already there, and the form works as one long page. A
 * wizard that leaves somebody staring at a blank box because a script failed
 * is worse than the wall of fields it replaced.
 *
 * ── VALIDATION ──────────────────────────────────────────────────────────
 *
 * A courtesy, not a gate. Every rule is enforced again in the portal against
 * the bytes themselves; this exists so nobody uploads 8MB over a phone
 * connection to be told no at the other end.
 */
(function () {
  'use strict';

  var MB = 1024 * 1024;
  var LAST = 4;

  function init(root) {
    if (!root || root.dataset.tgbkReady === '1') return;

    var form = root.querySelector('[data-tgbk-form]');
    if (!form) return;
    root.dataset.tgbkReady = '1';

    var holder = root.querySelector('[data-tgbk-turnstile]');
    var button = root.querySelector('[data-tgbk-submit]');
    var nextBtn = root.querySelector('[data-tgbk-next]');
    var backBtn = root.querySelector('[data-tgbk-back]');
    var beginBtn = root.querySelector('[data-tgbk-begin]');
    var status = root.querySelector('[data-tgbk-status]');
    var progress = root.querySelector('[data-tgbk-progress]');
    var progressLabel = root.querySelector('[data-tgbk-progress-label]');
    var summary = root.querySelector('[data-tgbk-summary]');
    var panels = {};
    var names = {};
    var all = form.querySelectorAll('[data-tgbk-panel]');
    for (var i = 0; i < all.length; i++) {
      var n = Number(all[i].dataset.tgbkPanel);
      panels[n] = all[i];
      names[n] = all[i].dataset.panelName || '';
    }

    var endpoint = root.dataset.endpoint;
    var sitekey = root.dataset.sitekey;
    var maxBytes = (parseInt(root.dataset.maxMb, 10) || 8) * MB;
    var widgetId = null;
    var sending = false;
    var step = 0;

    function say(text, tone) {
      if (!status) return;
      status.textContent = text || '';
      if (tone) status.setAttribute('data-tone', tone);
      else status.removeAttribute('data-tone');
    }

    function markBad(field, why) {
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); }
      }
      say(why, 'bad');
      return false;
    }

    function clearMarks() {
      var marked = form.querySelectorAll('[aria-invalid="true"]');
      for (var i = 0; i < marked.length; i++) marked[i].removeAttribute('aria-invalid');
    }

    function tooBig(input) {
      var f = input && input.files && input.files[0];
      return !!(f && f.size > maxBytes);
    }

    /* What the proof they have given is actually worth. Worked out from the
       fields, not from what they hoped for, so the last screen cannot promise
       100% to somebody who skipped the receipt. */
    function tier() {
      var e = form.elements;
      if (e.receipt.files && e.receipt.files.length) return { pc: 100, why: 'a charity shop receipt' };
      if (e.social_url.value.trim()) return { pc: 75, why: 'a photo and a public post' };
      return { pc: 50, why: 'a photo of the donation' };
    }

    function validate(which) {
      clearMarks();
      var e = form.elements;

      if (which === 1) {
        if (!e.reference.value.trim()) return markBad(e.reference, 'Please put in your order reference.');
        if (!e.email.value.trim() || e.email.value.indexOf('@') < 1) {
          return markBad(e.email, 'Please put in the email address you ordered with.');
        }
        return true;
      }
      if (which === 2) {
        if (!e.charity_shop.value.trim()) return markBad(e.charity_shop, 'Please tell us which charity shop.');
        if (!e.photo.files || !e.photo.files.length) return markBad(e.photo, 'Please add a photo of the donation.');
        if (tooBig(e.photo)) return markBad(e.photo, 'That photo is bigger than 8MB — please send a smaller one.');
        return true;
      }
      if (which === 3) {
        if (tooBig(e.receipt)) return markBad(e.receipt, 'That receipt is bigger than 8MB — please send a smaller one.');
        return true;
      }
      if (which === 4) {
        if (!token()) { say('Please complete the "I am human" check.', 'bad'); return false; }
        return true;
      }
      return true;
    }

    /* Everything they are about to send, in their own words, plus what it is
       worth. Built as DOM rather than a string of HTML — every value here was
       typed by a stranger. */
    function buildSummary() {
      if (!summary) return;
      summary.textContent = '';
      var e = form.elements;
      var t = tier();

      var badge = document.createElement('p');
      badge.className = 'tgbk__summary-tier';
      badge.textContent = 'You will get ' + t.pc + '% back — ' + t.why + '.';
      summary.appendChild(badge);

      var rows = [
        ['Order', e.reference.value.trim()],
        ['Email', e.email.value.trim()],
        ['Charity shop', [e.charity_shop.value.trim(), e.charity_town.value.trim()].filter(Boolean).join(', ')],
        ['Photo', (e.photo.files[0] || {}).name || '—'],
        ['Receipt', (e.receipt.files[0] || {}).name || 'none'],
        ['Post', e.social_url.value.trim() || 'none'],
      ];
      var dl = document.createElement('dl');
      dl.className = 'tgbk__summary-list';
      rows.forEach(function (r) {
        var dt = document.createElement('dt');
        dt.textContent = r[0];
        var dd = document.createElement('dd');
        dd.textContent = r[1];
        dl.appendChild(dt);
        dl.appendChild(dd);
      });
      summary.appendChild(dl);
    }

    function show(n) {
      step = n;
      for (var k in panels) {
        if (!Object.prototype.hasOwnProperty.call(panels, k)) continue;
        panels[k].classList.toggle('tgbk__panel--off', Number(k) !== n);
      }

      var inFlow = n >= 1;
      if (progress) progress.hidden = !inFlow;
      backBtn.hidden = n < 1;
      backBtn.disabled = n <= 1;
      nextBtn.hidden = !inFlow || n >= LAST;
      button.hidden = n !== LAST;

      if (inFlow && progressLabel) {
        progressLabel.textContent = 'Step ' + n + ' of ' + LAST + ' · ' + names[n];
        var segs = progress.querySelectorAll('.tgbk__progress-seg');
        for (var i = 0; i < segs.length; i++) {
          segs[i].classList.toggle('tgbk__progress-seg--on', Number(segs[i].dataset.seg) <= n);
        }
      }

      if (n === LAST) { buildSummary(); renderWidget(); }
      say('');

      if (n > 0) {
        var target = panels[n];
        if (target && target.scrollIntoView) target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        var first = target && target.querySelector('input:not([type=hidden])');
        if (first && n < LAST) { try { first.focus({ preventScroll: true }); } catch (e) { /* fine */ } }
      }
    }

    /* Turnstile loads async, so it may not be there when this first runs.
       onloadTurnstileCallback is the documented hook, but two forms on one
       page would fight over one global — polling for the object is duller and
       cannot be clobbered. Rendered at the LAST step rather than on load: a
       token goes stale in a few minutes, and somebody filling in a form on a
       phone can easily take longer than that. */
    function renderWidget() {
      if (!holder || !sitekey || widgetId !== null) return;
      if (!window.turnstile || typeof window.turnstile.render !== 'function') {
        window.setTimeout(renderWidget, 150);
        return;
      }
      widgetId = window.turnstile.render(holder, { sitekey: sitekey, theme: 'light', action: 'giveback' });
    }

    function token() {
      if (!window.turnstile || widgetId === null) return '';
      try { return window.turnstile.getResponse(widgetId) || ''; }
      catch (e) { return ''; }
    }

    /* A token is single-use. After any answer at all — accepted or refused —
       the old one is spent, so it is reset before the next attempt. Without
       this, a second try always fails the human check and looks like our bug. */
    function resetToken() {
      if (!window.turnstile || widgetId === null) return;
      try { window.turnstile.reset(widgetId); } catch (e) { /* nothing to do */ }
    }

    function finish(message) {
      var done = document.createElement('div');
      done.className = 'tgbk__done';
      var h = document.createElement('p');
      h.className = 'tgbk__done-t';
      h.textContent = 'Claim received';
      var b = document.createElement('p');
      b.className = 'tgbk__done-b';
      b.textContent = message;
      done.appendChild(h);
      done.appendChild(b);
      form.parentNode.replaceChild(done, form);
      done.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    if (beginBtn) beginBtn.addEventListener('click', function () { show(1); });
    nextBtn.addEventListener('click', function () { if (validate(step)) show(Math.min(LAST, step + 1)); });
    backBtn.addEventListener('click', function () { show(Math.max(1, step - 1)); });

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (sending) return;
      // Re-run every step, not just the last one. Somebody can go back, empty
      // a field and come forward again, and the last screen would never know.
      for (var s = 1; s <= LAST; s++) {
        if (!validate(s)) { if (s !== step) show(s); validate(s); return; }
      }

      sending = true;
      button.disabled = true;
      say('Sending…');

      var e = form.elements;
      var data = new FormData();
      data.append('reference', e.reference.value.trim());
      data.append('email', e.email.value.trim());
      data.append('charity_shop', e.charity_shop.value.trim());
      data.append('charity_town', e.charity_town.value.trim());
      data.append('social_url', e.social_url.value.trim());
      if (e.repost_consent.checked) data.append('repost_consent', '1');
      data.append('photo', e.photo.files[0]);
      if (e.receipt.files && e.receipt.files.length) data.append('receipt', e.receipt.files[0]);

      fetch(endpoint, { method: 'POST', headers: { 'X-Turnstile-Token': token() }, body: data })
        .then(function (res) {
          return res.json().then(function (body) { return { status: res.status, body: body || {} }; })
            .catch(function () { return { status: res.status, body: {} }; });
        })
        .then(function (out) {
          if (out.body.ok) { finish(out.body.message || 'Thanks — we will email you once we have checked it.'); return; }
          say(out.body.message || 'Something went wrong. Please try again.', 'bad');
          resetToken();
          sending = false;
          button.disabled = false;
        })
        .catch(function () {
          say('We could not reach us just now — please check your connection and try again.', 'bad');
          resetToken();
          sending = false;
          button.disabled = false;
        });
    });

    // Only now does the form become a wizard. Until this line runs, every
    // panel is on screen and the submit button works.
    form.classList.add('tgbk__form--stepped');
    show(0);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-towsons-giveback]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // The theme editor re-renders a section in place; without this the form
  // comes back with no listener and no widget after any settings change.
  document.addEventListener('shopify:section:load', boot);
})();
